import { extendTheme } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';
import '@fontsource/cairo/300.css';
import '@fontsource/cairo/400.css';
import '@fontsource/cairo/600.css';
import '@fontsource/cairo/700.css';

const theme = extendTheme({
  direction: 'rtl',
  fonts: {
    heading: `'Cairo', sans-serif`,
    body: `'Cairo', sans-serif`,
  },
  colors: {
    primary: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    },
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'primary',
      },
    },
    Select: {
      baseStyle: (props) => ({
        field: {
          borderRadius: 'lg',
          border: '1px solid',
          borderColor: mode('gray.200', 'gray.600')(props),
          bg: mode('white', 'gray.700')(props),
          color: mode('gray.800', 'white')(props),
          fontSize: 'md',
          fontWeight: '500',
          height: '48px',
          transition: 'all 0.2s ease-in-out',
          _hover: {
            borderColor: mode('primary.400', 'primary.400')(props),
            boxShadow: mode('0 2px 8px rgba(16, 185, 129, 0.15)', '0 2px 8px rgba(16, 185, 129, 0.3)')(props),
          },
          _focus: {
            borderColor: mode('primary.500', 'primary.500')(props),
            boxShadow: mode('0 0 0 3px rgba(16, 185, 129, 0.15)', '0 0 0 3px rgba(16, 185, 129, 0.3)')(props),
            outline: 'none',
          },
          _invalid: {
            borderColor: 'red.500',
          },
        },
        icon: {
          color: mode('primary.500', 'primary.200')(props),
          fontSize: 'xl',
        },
      }),
      defaultProps: {
        focusBorderColor: 'primary.500',
        errorBorderColor: 'red.500',
      },
    },
    Card: {
      baseStyle: (props) => ({
        container: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'white')(props),
          boxShadow: mode('md', 'dark-lg')(props),
        },
      }),
    },
    Table: {
      baseStyle: (props) => ({
        th: {
          bg: mode('gray.50', 'gray.700')(props),
          color: mode('gray.600', 'gray.300')(props),
          borderColor: mode('gray.200', 'gray.600')(props),
        },
        td: {
          color: mode('gray.700', 'gray.200')(props),
          borderColor: mode('gray.200', 'gray.600')(props),
        },
      }),
    },
    Menu: {
      baseStyle: (props) => ({
        list: {
          bg: mode('white', 'gray.800')(props),
          borderColor: mode('gray.200', 'gray.600')(props),
          color: mode('gray.800', 'gray.100')(props),
          boxShadow: mode('md', 'dark-lg')(props),
        },
        item: {
          bg: 'transparent',
          color: mode('gray.800', 'gray.100')(props),
          _hover: {
            bg: mode('gray.50', 'gray.700')(props),
          },
          _focus: {
            bg: mode('gray.50', 'gray.700')(props),
          },
        },
      }),
    },
    Popover: {
      baseStyle: (props) => ({
        content: {
          bg: mode('white', 'gray.800')(props),
          borderColor: mode('gray.200', 'gray.600')(props),
          color: mode('gray.800', 'gray.100')(props),
          boxShadow: mode('md', 'dark-lg')(props),
        },
        header: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        body: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        footer: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
      }),
    },
    AlertDialog: {
      baseStyle: (props) => ({
        overlay: {
          bg: mode('blackAlpha.600', 'blackAlpha.800')(props),
        },
        dialog: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        header: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        body: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        footer: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
      }),
    },
    Tooltip: {
      baseStyle: (props) => ({
        bg: mode('gray.800', 'gray.100')(props),
        color: mode('white', 'gray.800')(props),
      }),
    },
    Drawer: {
      baseStyle: (props) => ({
        overlay: {
          bg: mode('blackAlpha.600', 'blackAlpha.800')(props),
        },
        dialog: {
          bg: mode('white', 'gray.800')(props),
        },
        header: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        body: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        footer: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
      }),
    },
    Modal: {
      baseStyle: (props) => ({
        overlay: {
          bg: mode('blackAlpha.600', 'blackAlpha.800')(props),
        },
        dialog: {
          bg: mode('white', 'gray.800')(props),
        },
        header: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        body: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
        footer: {
          bg: mode('white', 'gray.800')(props),
          color: mode('gray.800', 'gray.100')(props),
        },
      }),
    },
    Badge: {
      baseStyle: (props) => ({
        // Ensure badge text is readable in both modes
      }),
      variants: {
        solid: (props) => {
          const colorScheme = props.colorScheme || 'gray';
          // For gray colorScheme
          if (colorScheme === 'gray') {
            return {
              bg: mode('gray.200', 'gray.600')(props),
              color: mode('gray.800', 'gray.100')(props),
            };
          }
          // For primary colorScheme - ensure good contrast in both modes
          if (colorScheme === 'primary') {
            return {
              bg: mode('primary.500', 'primary.600')(props),
              color: 'white',
            };
          }
          // For red colorScheme - lighter background in light mode for better visibility
          if (colorScheme === 'red') {
            return {
              bg: mode('red.100', 'red.600')(props),
              color: mode('red.800', 'white')(props),
            };
          }
          // For green colorScheme - lighter background in light mode for better visibility
          if (colorScheme === 'green') {
            return {
              bg: mode('green.100', 'green.600')(props),
              color: mode('green.800', 'white')(props),
            };
          }
          // For other colorSchemes, use default Chakra behavior
          return {};
        },
        subtle: (props) => {
          const colorScheme = props.colorScheme || 'gray';
          // For gray colorScheme
          if (colorScheme === 'gray') {
            return {
              bg: mode('gray.100', 'gray.700')(props),
              color: mode('gray.800', 'gray.100')(props),
            };
          }
          // For primary colorScheme - ensure good contrast in both modes
          if (colorScheme === 'primary') {
            return {
              bg: mode('primary.100', 'primary.900')(props),
              color: mode('primary.800', 'primary.100')(props),
            };
          }
          // For red colorScheme - subtle variant
          if (colorScheme === 'red') {
            return {
              bg: mode('red.50', 'red.900')(props),
              color: mode('red.800', 'red.100')(props),
            };
          }
          // For green colorScheme - subtle variant
          if (colorScheme === 'green') {
            return {
              bg: mode('green.50', 'green.900')(props),
              color: mode('green.800', 'green.100')(props),
            };
          }
          // For other colorSchemes, use default Chakra behavior
          return {};
        },
      },
    },
  },
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  styles: {
    global: (props) => ({
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : 'gray.50',
        color: props.colorMode === 'dark' ? 'white' : 'gray.800',
      },
    }),
  },
});

export default theme;

